
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent } from "@/components/ui/card";
import { Check, X, AlertCircle, Package } from "lucide-react";
import { useBulkAppImport } from "@/hooks/useBulkAppImport";

interface BulkAppImportDialogProps {
  onClose: () => void;
  onImportComplete?: () => void;
}

export const BulkAppImportDialog = ({ onClose, onImportComplete }: BulkAppImportDialogProps) => {
  const [bundleIdText, setBundleIdText] = useState("");
  const { toast } = useToast();
  
  const {
    importApps,
    isImporting,
    progress,
    results,
    reset
  } = useBulkAppImport();

  const handleImport = async () => {
    if (!bundleIdText.trim()) {
      toast({
        title: "Error",
        description: "Please enter at least one bundle ID",
        variant: "destructive",
      });
      return;
    }

    // Parse bundle IDs from text input
    const bundleIds = bundleIdText
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0)
      .map(line => {
        // Handle format: "bundle.id | App Name" or just "bundle.id"
        const parts = line.split('|').map(part => part.trim());
        return {
          bundleId: parts[0],
          suggestedName: parts[1] || null
        };
      });

    if (bundleIds.length === 0) {
      toast({
        title: "Error",
        description: "No valid bundle IDs found",
        variant: "destructive",
      });
      return;
    }

    try {
      await importApps(bundleIds);
      
      if (onImportComplete) {
        onImportComplete();
      }
      
      toast({
        title: "Import Complete",
        description: `Processed ${bundleIds.length} apps`,
      });
    } catch (error) {
      console.error('Import failed:', error);
      toast({
        title: "Import Failed",
        description: "Failed to import apps",
        variant: "destructive",
      });
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <Check className="w-4 h-4 text-green-600" />;
      case 'error':
        return <X className="w-4 h-4 text-red-600" />;
      case 'skipped':
        return <AlertCircle className="w-4 h-4 text-yellow-600" />;
      default:
        return <Package className="w-4 h-4 text-gray-400" />;
    }
  };

  return (
    <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Bulk Import Apps</DialogTitle>
      </DialogHeader>
      
      <div className="grid gap-4 py-4">
        <div>
          <label className="text-sm font-medium text-gray-700 mb-2 block">
            Bundle IDs (one per line)
          </label>
          <Textarea
            value={bundleIdText}
            onChange={(e) => setBundleIdText(e.target.value)}
            placeholder="com.apple.Safari&#10;com.microsoft.teams | Microsoft Teams&#10;com.spotify.client"
            className="min-h-[200px]"
            disabled={isImporting}
          />
          <p className="text-xs text-gray-500 mt-1">
            Format: "bundle.id" or "bundle.id | App Name"
          </p>
        </div>

        {isImporting && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">
                Importing apps... ({progress.processed}/{progress.total})
              </span>
              <span className="text-sm text-gray-500">
                {Math.round((progress.processed / progress.total) * 100)}%
              </span>
            </div>
            <Progress value={(progress.processed / progress.total) * 100} />
          </div>
        )}

        {results.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-gray-700">Import Results</h4>
            <div className="max-h-[300px] overflow-y-auto space-y-2">
              {results.map((result, index) => (
                <Card key={index} className="p-0">
                  <CardContent className="p-3">
                    <div className="flex items-center gap-3">
                      {getStatusIcon(result.status)}
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium truncate">
                            {result.appName || result.bundleId}
                          </span>
                          {result.appName && (
                            <span className="text-xs text-gray-500 truncate">
                              {result.bundleId}
                            </span>
                          )}
                        </div>
                        
                        {result.message && (
                          <p className="text-xs text-gray-600 mt-1">
                            {result.message}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
      
      <div className="flex justify-end space-x-2">
        <Button variant="outline" onClick={handleClose}>
          {isImporting ? "Cancel" : "Close"}
        </Button>
        <Button 
          onClick={handleImport}
          disabled={isImporting || !bundleIdText.trim()}
        >
          {isImporting ? "Importing..." : "Import Apps"}
        </Button>
      </div>
    </DialogContent>
  );
};
